import {useTranslation} from "react-i18next";
import React from "react";
import {Box, List, ListItem, Tab, Tabs, Typography, useTheme} from "@mui/material";
import PropTypes from "prop-types";
import {makeRequest} from "../../../../services/requestService";
import {API_URL} from "../../../../constants";
import {SelectLabelSet, SelectLabelSetSkeleton} from "./SelectLabelSet";
import PopoverSearchInput from "../../../Search/PopoverSearchInput";
import Toolbar from "@mui/material/Toolbar";
import InfiniteScroll from "react-infinite-scroll-component";

AnnotatorSetList.propTypes = {
    selected: PropTypes.array,
    onChange: PropTypes.func
}

AnnotatorSetList.defaultProps = {
    selected: [],
    onChange: () => null
}

export default function AnnotatorSetList({selected, onChange}) {
    const {t} = useTranslation('common');
    const SIZE = 12;
    const theme = useTheme();
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [tabValue, setTabValue] = React.useState(0);
    const [searchValue, setSearchValue] = React.useState("");
    const [totalPages, setTotalPages] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(0);

    const fetchData = async (page = 0) => {
        let url;
        if (tabValue) {
            if (searchValue)
                url = `${API_URL}/label/set/search?addPrivate=false&addPublic=true&page=${page}&query=${searchValue}&size=${SIZE}`;
            else
                url = `${API_URL}/label/sets/public?page=${page}&size=${SIZE}`;
        } else {
            if (searchValue)
                url = `${API_URL}/label/set/search?addPrivate=true&addPublic=false&page=${page}&query=${searchValue}&size=${SIZE}`;
            else
                url = `${API_URL}/my/label/set?page=${page}&size=${SIZE}`;
        }
        return makeRequest(url);
    }

    React.useEffect(() => {
        setLoading(true);
        let timeoutId;
        fetchData().then((response) => {
            if (response.status === 200 && Array.isArray(response.data.data)) {
                setData(response.data.data);
                setTotalPages(response.data.totalPages);
                setCurrentPage(response.data.currentPage);
                timeoutId = window.setTimeout(() => {
                    setLoading(false);
                }, 200);
            }
        });
        return () => {
            window.clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue]);


    const fetchMore = () => {
        fetchData(currentPage + 1).then((response) => {
            if (response.status === 200 && Array.isArray(response.data.data)) {
                setData(prevState => [...prevState, ...response.data.data]);
                setTotalPages(response.data.totalPages);
                setCurrentPage(response.data.currentPage);
            }
        });
    }

    const handleCheck = (labelSet) => {
        let elem = selected.find(ss => ss.id === labelSet.id);
        if (elem)
            onChange(selected.filter(ss => ss.id !== labelSet.id));
        else {
            onChange([...selected, labelSet]);
        }
    }

    const handleSearch = () => {
        fetchData().then((response) => {
            if (response.status === 200 && Array.isArray(response.data.data)) {
                setData(response.data.data);
                setTotalPages(response.data.totalPages);
                setCurrentPage(response.data.currentPage);
            }
        });
    }

    return (
        <>
            <Toolbar sx={{justifyContent: "space-between", borderBottom: `solid 2px ${theme.palette.secondary.dark}`}}>
                <Typography variant="h6" color="textSecondary" fontWeight="bold" textAlign="center">
                    {t("main.components.annotator.sets.title")}
                </Typography>
                <PopoverSearchInput
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    onSubmit={handleSearch}/>
            </Toolbar>
            <Tabs
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}
                aria-label="label set selection"
                sx={{
                    borderBottom: theme => `solid 1px ${theme.palette.divider}`,
                    mb: 1
                }}
            >
                <Tab label={t("main.components.annotator.sets.tabs.mine")}/>
                <Tab label={t("main.components.annotator.sets.tabs.public")}/>
            </Tabs>
            <Box
                sx={{
                    flexGrow: 1,
                    overflow: "hidden",
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    minHeight: "200px"
                }}
            >
                <List
                    id="annotSetList"
                    sx={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        borderRadius: '0.3em',
                        overflow: 'auto',
                        bgcolor: theme => theme.palette.mode === "light" ? theme.palette.background.default : undefined
                    }}>
                    <InfiniteScroll
                        next={fetchMore}
                        hasMore={currentPage + 1 < totalPages}
                        dataLength={data.length}
                        scrollableTarget="annotSetList"
                        style={{overflow: "hidden"}}
                        loader={
                            Array.from({length: SIZE}).map((value, index) => <ListItem
                                key={`annotListLoader${index}`}
                            >
                                <SelectLabelSetSkeleton/>
                            </ListItem>)
                        }
                    >
                        {!loading && Array.isArray(data) && data.map((set) => (
                            <ListItem key={`annotator-label-set-${set.id}`}>
                                <SelectLabelSet
                                    element={set}
                                    checked={Boolean(selected.find(ls => ls.id === set.id))}
                                    onCheck={() => handleCheck(set)}/>
                            </ListItem>
                        ))}
                    </InfiniteScroll>
                </List>
            </Box>
        </>
    );
}
